import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  DoCheck,
  Output,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
  IterableDiffers,
  IterableDiffer
} from '@angular/core';
import { Observable } from 'rxjs';
import { AccountsService } from '~/services/administration.ts/accounts';
import { SitesEnum } from '~/shared/enums/sites.enum';
import { SelectableSettings } from '@progress/kendo-angular-grid';

export interface IAccountSiteSelection {
  accountId: number;
  siteId: number;
  account?: any;
  site?: any;
}

@Component({
    selector: 'asap-account-sites-selection',
    templateUrl: './account-sites-selection.component.html',
    styleUrls: ['./account-sites-selection.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AccountSitesSelectionComponent implements OnInit, OnChanges, DoCheck {
  private _accountsService = inject(AccountsService);
  private _differs = inject(IterableDiffers);
  private _accountsDiffer?: IterableDiffer<any>;

  @Input() selectedAccounts: any[] = [];
  @Input() selectedKeys: any[] = [];
  @Input() singleSelect: boolean = false;
  @Input() disabled: boolean = false;
  @Input() enableSiteSelection: boolean = true;
  @Input() filteringEnable: boolean = true;
  @Input() selectByFieldTitle: string = 'Select Account';
  @Input() resolver?: (params?: any, model?: any) => Observable<any>;

  @Output() onRowSelectionChanges = new EventEmitter<any[]>();
  @Output() selectedAccountsChange = new EventEmitter<any[]>();

  _accounts: any[] = [];
  _filteredAccounts: any[] = [];
  _selectedAccountIds: any[] = [];
  _searchText: string = '';
  _isLoading: boolean = false;

  get selectableSettings(): SelectableSettings {
    return {
      checkboxOnly: true,
      mode: this.singleSelect ? 'single' : 'multiple',
      enabled: !this.disabled
    };
  }

  ngOnInit(): void {
    this._accountsDiffer = this._differs.find([]).create();
    this.loadAccounts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedAccounts'] || changes['selectedKeys']) {
      this._syncSelectedAccounts();
    }
  }

  ngDoCheck(): void {
    if (this._accountsDiffer) {
      const source = (this.selectedAccounts && this.selectedAccounts.length > 0)
        ? this.selectedAccounts
        : this.selectedKeys;
      if (Array.isArray(source)) {
        const diff = this._accountsDiffer.diff(source);
        if (diff) {
          this._syncSelectedAccounts();
        }
      }
    }
  }

  loadAccounts(): void {
    this._isLoading = true;
    const fetch$ = this.resolver
      ? this.resolver()
      : this._accountsService.getAccounts();
    fetch$.subscribe({
      next: (res: any) => {
        const rawData = res?.data?.value ?? res?.data ?? res ?? [];
        this._accounts = Array.isArray(rawData) ? rawData : [];
        this._applyFilter();
        this._syncSelectedAccounts();
        this._isLoading = false;
      },
      error: () => {
        this._isLoading = false;
      }
    });
  }

  _isAccountSelected(id: any): boolean {
    if (id == null) {
      return false;
    }
    return this._selectedAccountIds.some((x) => +x === +id);
  }

  private _extractAccountId(item: any): number | null {
    if (item == null) {
      return null;
    }
    const id = item?.accountId != null
      ? +item.accountId
      : (item?.account?.id != null ? +item.account.id : (item?.id != null ? +item.id : +item));
    return id != null && !isNaN(id) ? id : null;
  }

  private _extractSiteId(item: any): number | null {
    if (item == null) {
      return null;
    }
    const id = item?.siteId != null
      ? +item.siteId
      : (item?.site?.id != null ? +item.site.id : null);
    return id != null && !isNaN(id) ? id : null;
  }

  _onSearchChange(term: string): void {
    this._searchText = term ?? '';
    this._applyFilter();
  }

  _applyFilter(): void {
    if (!this._searchText || !this._searchText.trim()) {
      this._filteredAccounts = [...this._accounts];
      return;
    }
    const lower = this._searchText.trim().toLowerCase();
    this._filteredAccounts = this._accounts.filter((acc) =>
      acc.name?.toLowerCase().includes(lower)
    );
  }

  _syncSelectedAccounts(): void {
    const source = (this.selectedAccounts && this.selectedAccounts.length > 0)
      ? this.selectedAccounts
      : this.selectedKeys;

    if (!source || !Array.isArray(source) || source.length === 0) {
      this._selectedAccountIds = [];
      return;
    }

    const ids: number[] = [];
    for (const item of source) {
      const accountId = this._extractAccountId(item);
      if (accountId != null && !isNaN(accountId)) {
        ids.push(accountId);
      }
    }
    this._selectedAccountIds = ids;

    if (this._accounts && this._accounts.length > 0) {
      for (const account of this._accounts) {
        const match = source.find((s: any) => {
          const sAccId = this._extractAccountId(s);
          return sAccId != null && sAccId === +account.id;
        });

        if (match) {
          const targetSiteId = this._extractSiteId(match);
          if (targetSiteId != null && targetSiteId !== -1 && account.sites && account.sites.length > 0) {
            account.$selectedSiteId =
              account.sites.find((site: any) => +site.id === targetSiteId) ??
              match.site ??
              account.sites[0];
          } else if (account.sites && account.sites.length > 0) {
            account.$selectedSiteId = account.sites[0];
          } else {
            account.$selectedSiteId = null;
          }
        }
      }
    }
  }

  _onRowSelectionChange(selectedKeys: any[]): void {
    if (this.disabled) {
      return;
    }

    this._selectedAccountIds = (selectedKeys || []).map((x) => +x);

    for (const id of this._selectedAccountIds) {
      const account = this._accounts.find((a) => +a.id === id);
      if (account && !account.$selectedSiteId && account.sites && account.sites.length > 0) {
        account.$selectedSiteId = account.sites[0];
      }
    }

    this._emitChanges();
  }

  _onSiteChange(selectedSite: any, account: any): void {
    account.$selectedSiteId = selectedSite;

    if (!this._isAccountSelected(account.id)) {
      if (this.singleSelect) {
        this._selectedAccountIds = [+account.id];
      } else {
        this._selectedAccountIds = [...this._selectedAccountIds, +account.id];
      }
    }

    this._emitChanges();
  }

  private _emitChanges(): void {
    if (!this.enableSiteSelection) {
      this.onRowSelectionChanges.emit(this._selectedAccountIds);
      this.selectedAccountsChange.emit(this._selectedAccountIds);
      return;
    }

    const selectedResult: IAccountSiteSelection[] = this._selectedAccountIds
      .map((accId) => {
        const acc = this._accounts.find((a) => +a.id === +accId);
        const site = acc?.$selectedSiteId;
        const siteId = site
          ? (typeof site === 'object' ? (site.id != null ? +site.id : -1) : +site)
          : (acc?.sites && acc.sites.length > 0 ? -1 : null);
        const siteObj = typeof site === 'object'
          ? site
          : (acc?.sites?.find((s: any) => +s.id === siteId) ?? null);
        return {
          accountId: +accId,
          siteId: siteId,
          account: acc,
          site: siteObj
        };
      })
      .filter((item) => item.accountId != null && !isNaN(item.accountId));

    this.onRowSelectionChanges.emit(selectedResult);
    this.selectedAccountsChange.emit(selectedResult);
  }
}
